import { useSelector } from 'react-redux';
import { betreuerByNrSelector, skontodefinitionenByArtSelector } from 'store/lookups/selector';
import { useRechnungList } from 'api/rechnung';
import * as React from 'react';
import { useBetreuerprovisionListByNr } from 'api/betreuerprovision';
import { RechnungPositionFilter, useRechnungPositionList } from 'api/rechnungPosition';
import { RechnungSummary, defaultLineData, FilterValues, LineData } from '../types';
import { getBetreuerAnteil } from '../utils';

export interface RechnungSummaryProps extends FilterValues {
    enabled: boolean;
}

export const useRechnungSummary = ({
    statusList,
    datumVon,
    datumBis,
    kNr,
    konzernNr,
    kundePlzVon,
    kundePlzBis,
    kundeUmsatzTsdVon,
    kundeUmsatzTsdBis,
    kundeBranchen,
    kundeErweiterteSuchen,
    kundeInteresseFuerProdukte,
    kundeBetreuer,
    kundeTelBetreuer,
    betreuer,
    betreuerGebiet,
    telBetreuer,
    telBetreuerGebiet,
    produktgruppen,
}: RechnungSummaryProps): RechnungSummary => {
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const betreuerprovisionListByNr = useBetreuerprovisionListByNr();
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);

    const rechnungList = useRechnungList(
        {
            statusList,
            datumVon,
            datumBis,
            kNr,
            konzernNr,
            kundePlzVon,
            kundePlzBis,
            kundeUmsatzVon: kundeUmsatzTsdVon ? kundeUmsatzTsdVon * 1000 : undefined,
            kundeUmsatzBis: kundeUmsatzTsdBis ? kundeUmsatzTsdBis * 1000 : undefined,
            kundeBranchen,
            kundeErweiterteSuchen,
            kundeInteresseFuerProdukte,
            kundeBetreuerExact: kundeBetreuer,
            kundeTelBetreuerExact: kundeTelBetreuer,
            betreuerExact: betreuer,
            telBetreuerExact: telBetreuer,
            produktgruppen,
            withKunde: true,
        },
        {},
    );

    const rechnungPositionFilter = React.useMemo<RechnungPositionFilter>(
        () => ({ rNrList: rechnungList.data?.map((r) => r.nr) || [], withArtikelDetail: true }),
        [rechnungList],
    );
    const rechnungPositionList = useRechnungPositionList(rechnungPositionFilter, { enabled: !!rechnungList.data });

    return React.useMemo<RechnungSummary>(() => {
        const total: LineData = { ...defaultLineData };
        const betreuerTotal: {
            [key: number]: LineData;
        } = {};
        const telBetreuerTotal: {
            [key: number]: LineData;
        } = {};

        const rechnungLineDataList: LineData[] = [];

        const usedProduktgruppen: Record<string, number> = {};

        (rechnungList.data || [])
            .sort((a, b) => (a.datum + a.nr).localeCompare(b.datum + b.nr))
            .forEach((rechnung) => {
                /*
                if (![0, 1, 5, 6].includes(rechnung.status)) {
                    return;
                }
                 */

                if (betreuer && betreuerGebiet && rechnung.kunde?.betreuer !== betreuer) {
                    return;
                }

                if (telBetreuer && telBetreuerGebiet && rechnung.kunde?.telBetreuer !== telBetreuer) {
                    return;
                }

                const filteredRechnungPositionList = (rechnungPositionList.data || []).filter(
                    (rp) => rp.rNr === rechnung.nr,
                );

                if (filteredRechnungPositionList.length === 0) {
                    return;
                }

                const rechnungLineData: LineData = {
                    ...defaultLineData,
                    rNr: rechnung.nr,
                    datum: rechnung.datum,
                    kNr: rechnung.kNr || 0,
                    kName: rechnung.kunde?.name1 || '',
                };

                const skontoP = skontodefinitionenByArt[rechnung.zahlBed]?.skP1 || 0;
                // const provision = betreuerByNr[rechnung.betreuer]?.provision || 0;

                let ekNetto = 0;
                let vkNetto = 0;

                filteredRechnungPositionList.forEach((rechnungPosition) => {
                    ekNetto += rechnungPosition.posEkPreis * rechnungPosition.posMenge;
                    vkNetto += rechnungPosition.posEPreis * rechnungPosition.posMenge;

                    if (rechnungPosition.artikelDetail?.produktGruppeNr) {
                        if (!usedProduktgruppen[rechnungPosition.artikelDetail?.produktGruppeNr]) {
                            usedProduktgruppen[rechnungPosition.artikelDetail?.produktGruppeNr] = 0;
                        }
                        usedProduktgruppen[rechnungPosition.artikelDetail?.produktGruppeNr] += 1;
                    }
                });

                const tipp = rechnung.tipp === 0 ? 0 : (vkNetto / 100) * rechnung.tipp;
                const skonto = skontoP === 0 ? 0 : (vkNetto / 100) * skontoP;
                const bMarge = vkNetto - ekNetto;
                const nMarge = bMarge - tipp - skonto - rechnung.fracht - rechnung.roadPr;

                // const bAnteil = (nMarge / 100) * provision;
                const { value: bAnteil } = getBetreuerAnteil(
                    betreuerprovisionListByNr,
                    rechnung.betreuer,
                    rechnung.datum,
                    (betreuerTotal[rechnung.betreuer]?.nMarge || 0) + nMarge,
                    betreuerTotal[rechnung.betreuer]?.bAnteil || 0,
                );
                const fAnteil = nMarge - bAnteil;

                total.count += 1;

                total.ekNetto += ekNetto;
                rechnungLineData.ekNetto = ekNetto;

                total.vkNetto += vkNetto;
                rechnungLineData.vkNetto = vkNetto;

                total.bMarge += bMarge;
                rechnungLineData.bMarge = bMarge;

                total.tippProv += tipp;
                rechnungLineData.tippProv = tipp;

                total.skonto += skonto;
                rechnungLineData.skonto = skonto;

                total.fracht += rechnung.fracht + rechnung.roadPr;
                rechnungLineData.fracht = rechnung.fracht + rechnung.roadPr;

                total.nMarge += nMarge;
                rechnungLineData.nMarge = nMarge;

                total.bAnteil += bAnteil;
                rechnungLineData.bAnteil = bAnteil;

                total.fAnteil += fAnteil;
                rechnungLineData.fAnteil = fAnteil;

                /*
                if (rechnung.betrUm) {
                    total.betrUmCount += 1;
                    total.betrUm += vkNetto;
                }
                 */

                if (rechnung.betreuer) {
                    if (!betreuerTotal[rechnung.betreuer]) {
                        betreuerTotal[rechnung.betreuer] = {
                            ...defaultLineData,
                            name: betreuerByNr[rechnung.betreuer]?.name || betreuerByNr[rechnung.betreuer]?.kz,
                        };
                    }

                    betreuerTotal[rechnung.betreuer].betreuerNr = rechnung.betreuer;
                    betreuerTotal[rechnung.betreuer].count += 1;
                    betreuerTotal[rechnung.betreuer].ekNetto += ekNetto;
                    betreuerTotal[rechnung.betreuer].vkNetto += vkNetto;
                    betreuerTotal[rechnung.betreuer].bMarge += bMarge;
                    betreuerTotal[rechnung.betreuer].tippProv += tipp;
                    betreuerTotal[rechnung.betreuer].skonto += skonto;
                    betreuerTotal[rechnung.betreuer].fracht += rechnung.fracht + rechnung.roadPr;
                    betreuerTotal[rechnung.betreuer].nMarge += nMarge;
                    betreuerTotal[rechnung.betreuer].bAnteil += bAnteil;
                    betreuerTotal[rechnung.betreuer].fAnteil += fAnteil;
                }

                if (rechnung.telBetreuer) {
                    if (!telBetreuerTotal[rechnung.telBetreuer]) {
                        telBetreuerTotal[rechnung.telBetreuer] = {
                            ...defaultLineData,
                            name: betreuerByNr[rechnung.telBetreuer]?.name,
                        };
                    }

                    telBetreuerTotal[rechnung.telBetreuer].betreuerNr = rechnung.telBetreuer;
                    telBetreuerTotal[rechnung.telBetreuer].count += 1;
                    telBetreuerTotal[rechnung.telBetreuer].ekNetto += ekNetto;
                    telBetreuerTotal[rechnung.telBetreuer].vkNetto += vkNetto;
                    telBetreuerTotal[rechnung.telBetreuer].bMarge += bMarge;
                    telBetreuerTotal[rechnung.telBetreuer].tippProv += tipp;
                    telBetreuerTotal[rechnung.telBetreuer].skonto += skonto;
                    telBetreuerTotal[rechnung.telBetreuer].fracht += rechnung.fracht + rechnung.roadPr;
                    telBetreuerTotal[rechnung.telBetreuer].nMarge += nMarge;
                    // telBetreuerTotal[rechnung.telBetreuer].bAnteil += bAnteil;
                    telBetreuerTotal[rechnung.telBetreuer].fAnteil += fAnteil;
                }

                rechnungLineDataList.push(rechnungLineData);
            });

        let fAnteilTotal = 0;
        Object.values(betreuerTotal).forEach((item) => {
            const provision = betreuerByNr[item.betreuerNr]?.provision || 0;
            if (provision >= 100) {
                fAnteilTotal += item.bAnteil;
            } else {
                fAnteilTotal += item.fAnteil;
            }
        });

        return {
            loading: rechnungList.isLoading,
            count: total.count, // rechnungList.data?.length || 0,
            total,
            betreuerTotal,
            telBetreuerTotal,
            rechnungLineDataList,
            fAnteilTotal,
            usedProduktgruppen,
        };
    }, [
        betreuerprovisionListByNr,
        rechnungList.isLoading,
        rechnungList.data,
        rechnungPositionList.data,
        skontodefinitionenByArt,
        betreuerByNr,
        betreuer,
        betreuerGebiet,
        telBetreuer,
        telBetreuerGebiet,
    ]);
};
